import { CopyIcon, EmailIcon, FacebookIcon, LinkedInIcon, TelegramIcon, WhatsAppIcon, XIcon } from '@/utils/client/svg';
import { Button, ButtonIcon } from '@habacuscode/habacus_ui_component_library';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

type ShareModalProps = {
  isOpen: boolean;
  onClose: () => void;

  link: string
};

type ShareButtonProps = {
  className?: string;
  label?: string;
  link: string;
  isOnlyIcon?: boolean;
};

// Share Modal Component
const ShareModal = ({ isOpen, onClose, link }: ShareModalProps) => {
  const tCommon = useTranslations('Common');
  const tCta = useTranslations('Cta');

  const [copied, setCopied] = useState(false);

  const pageTitle = typeof document !== 'undefined' ? document.title : 'Check this out!';

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = link;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();

      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Failed to copy', fallbackErr);
      }

      document.body.removeChild(textArea);
    }
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(pageTitle);
    const body = encodeURIComponent(`Dai un'occhiata a questo link: ${link}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(`${pageTitle} ${link}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareViaFacebook = () => {
    const url = encodeURIComponent(link);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
  };

  const shareViaTwitterX = () => {
    const text = encodeURIComponent(pageTitle);
    const url = encodeURIComponent(link);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'width=600,height=400');
  };

  const shareViaLinkedIn = () => {
    const url = encodeURIComponent(link);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=600,height=400');
  };

  const shareViaTelegram = () => {
    const text = encodeURIComponent(`${pageTitle} ${link}`);
    window.open(`https://t.me/share/url?url=${link}&text=${text}`, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 relative">
        {/* Close Button */}
        <Button
          name="share_results_close"
          variant="ghost"
          size="sm"
          type="button"
          onClick={onClose}
          className="absolute top-0 right-0 mt-2 mr-2 text-gray-400 hover:text-gray-600 focus:outline-hidden focus:shadow-none"
          style={{
            boxShadow: 'none !important'
          }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </Button>

        {/* Modal Header */}
        <h3 className="text-lg font-semibold text-blue-950 mb-4">{tCommon('shareLink')}</h3>

        {/* Share Options */}
        <div className="space-y-3">
          {/* Copy Link */}
          <Button
            name="share_results_copy"
            variant="ghost"
            size="md"
            type="button"
            onClick={copyToClipboard}
            className="w-full flex items-center gap-3 p-3 hover:bg-gray-50"
          >
            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
              <CopyIcon />
            </div>
            {copied ? tCta('copiedLink') : tCta('copyLink')}
          </Button>

          {/* Email */}
          <Button
            name="share_results_email"
            variant="ghost"
            size="md"
            type="button"
            onClick={shareViaEmail}
            className="w-full flex items-center gap-3 p-3 hover:bg-gray-50"
          >
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <EmailIcon />
            </div>
            Email
          </Button>

          {/* WhatsApp */}
          <Button
            name="share_results_whatsapp"
            variant="ghost"
            size="md"
            type="button"
            onClick={shareViaWhatsApp}
            className="w-full flex items-center gap-3 p-3 hover:bg-gray-50"
          >
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <WhatsAppIcon />
            </div>
            WhatsApp
          </Button>

          {/* Facebook */}
          <Button
            name="share_results_facebook"
            variant="ghost"
            size="md"
            type="button"
            onClick={shareViaFacebook}
            className="w-full flex items-center gap-3 p-3 hover:bg-gray-50"
          >
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <FacebookIcon />
            </div>
            Facebook
          </Button>

          {/* Twitter */}
          <Button
            name="share_results_twitter_x"
            variant="ghost"
            size="md"
            type="button"
            onClick={shareViaTwitterX}
            className="w-full flex items-center gap-3 p-3 hover:bg-gray-50"
          >
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <XIcon />
            </div>
            X
          </Button>

          {/* LinkedIn */}
          <Button
            name="share_results_linkedin"
            variant="ghost"
            size="md"
            type="button"
            onClick={shareViaLinkedIn}
            className="w-full flex items-center gap-3 p-3 hover:bg-gray-50"
          >
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <LinkedInIcon />
            </div>
            LinkedIn
          </Button>

          {/* Telegram */}
          <Button
            name="share_results_telegram"
            variant="ghost"
            size="md"
            type="button"
            onClick={shareViaTelegram}
            className="w-full flex items-center gap-3 p-3 hover:bg-gray-50"
          >
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <TelegramIcon />
            </div>
            Telegram
          </Button>
        </div>
      </div>
    </div>
  );
};

// Share Button Component
const ShareButton = ({ className = "", link, isOnlyIcon = false }: ShareButtonProps) => {
  const tCta = useTranslations('Cta');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const icon: string = `fa-regular fa-share-from-square ${className} text-blue-950`;

  return (
    <>
      {
        isOnlyIcon ?
          <ButtonIcon
            name="shareButton"
            variant="ghost"
            icon={icon}
            onClick={() => setIsModalOpen(true)} />
          : <Button
            name="share"
            variant='ghost'
            icon={icon}
            onClick={() => setIsModalOpen(true)}
            className='whitespace-nowrap'
          >
          </Button>
      }


      <ShareModal
        isOpen={isModalOpen}
        link={link}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export { ShareButton, ShareModal };